import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import Navigation from "@/components/Navigation";
import Login from "@/pages/Login";
import StudentLogin from "@/pages/StudentLogin";
import Signup from "@/pages/Signup";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import Students from "@/pages/Students";
import StudentDetail from "@/pages/StudentDetail";
import StudentAccounts from "@/pages/StudentAccounts";
import StudentDashboard from "@/pages/StudentDashboard";
import Teachers from "@/pages/Teachers";
import SalaryTracker from "@/pages/SalaryTracker";
import AdminDataIntegrity from "@/pages/AdminDataIntegrity";
import Welcome from "@/pages/Welcome";
import Settings from "@/pages/Settings";
import TimeClock from "@/pages/TimeClock";
import TimeManagement from "@/pages/TimeManagement";
import UserManagement from "@/pages/UserManagement";
import Schedule from "@/pages/Schedule";
import InactiveStudents from "@/pages/InactiveStudents";
import ActiveStudents from "@/pages/ActiveStudents";
import DeletedStudents from "@/pages/DeletedStudents";
import TrialBooking from "@/pages/TrialBooking";
import TrialBookings from "@/pages/TrialBookings";
import PaymentInstructions from "@/pages/PaymentInstructions";
import SubscriptionPage from "@/pages/SubscriptionPage";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PayMongoResult from "@/pages/PayMongoResult";
import { PaymentErrorBoundary } from "@/components/PaymentErrorBoundary";
import AdminFxRate from "@/pages/AdminFxRate";
import AdminPaymongoKeys from "@/pages/AdminPaymongoKeys";
import AdminReminders from "@/pages/AdminReminders";
import MySalary from "@/pages/MySalary";
import AdminPayments from "@/pages/AdminPayments";
import ApplyPage from "@/pages/ApplyPage";
import Recruitment from "@/pages/Recruitment";
import RecruitmentDetail from "@/pages/RecruitmentDetail";
import SalesAnalytics from "@/pages/SalesAnalytics";
import TutorProfiles from "@/pages/TutorProfiles";
import TutorDetail from "@/pages/TutorDetail";
import AdminTutorProfiles from "@/pages/AdminTutorProfiles";
import MyAvailability from "@/pages/MyAvailability";
import ReferralPage from "@/pages/ReferralPage";
import TutorMessages from "@/pages/TutorMessages";
import AdminMessageMonitor from "@/pages/AdminMessageMonitor";
import AdminScheduleRepair from "@/pages/AdminScheduleRepair";
import AdminUntaggedAlerts from "@/pages/AdminUntaggedAlerts";

// Protected Route component
function ProtectedRoute({ children, adminOnly = false, allowedRoles = [] }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-pulse text-slate-400">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Check role-based access
  if (adminOnly && user.role !== "admin") {
    return <Navigate to="/schedule" replace />;
  }

  if (allowedRoles.length > 0 && !allowedRoles.includes(user.role)) {
    // Redirect based on role
    if (user.role === "student") {
      return <Navigate to="/student-dashboard" replace />;
    }
    return <Navigate to="/schedule" replace />;
  }

  return children;
}

// Student Protected Route - only for students
function StudentRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-pulse text-slate-400">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/student-login" replace />;
  }

  if (user.role !== "student") {
    return <Navigate to="/" replace />;
  }

  return children;
}

// Layout component with navigation
function Layout({ children }) {
  const { user } = useAuth();
  
  if (!user) return children;
  
  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {children}
      </main>
    </div>
  );
}

// Picks the correct Messages screen based on role:
//  • admin  → AdminMessageMonitor (read-only audit of every tutor's chats)
//  • tutor  → TutorMessages (today-only student dropdown)
function MessagesRouter() {
  const { user } = useAuth();
  if (user?.role === "admin") return <AdminMessageMonitor />;
  return <TutorMessages />;
}

function AppRoutes() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-pulse text-slate-400">Loading...</div>
      </div>
    );
  }

  // Helper to get default redirect based on role
  const getDefaultRoute = () => {
    if (!user) return "/login";
    if (user.role === "admin") return "/";
    if (user.role === "student") return "/student-dashboard";
    return "/schedule";
  };

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={
        user ? <Navigate to={getDefaultRoute()} replace /> : <Login />
      } />
      <Route path="/student-login" element={
        user ? <Navigate to={getDefaultRoute()} replace /> : <StudentLogin />
      } />
      <Route path="/signup" element={
        user ? <Navigate to={getDefaultRoute()} replace /> : <Signup />
      } />
      <Route path="/forgot-password" element={
        user ? <Navigate to={getDefaultRoute()} replace /> : <ForgotPassword />
      } />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/apply" element={<ApplyPage />} />
      <Route path="/book-trial" element={<TrialBooking />} />
      <Route path="/tutors" element={<TutorProfiles />} />
      <Route path="/welcome" element={<Welcome />} />
      <Route path="/tutors/:id" element={<TutorDetail />} />
      <Route path="/payment" element={<PaymentInstructions />} />
      <Route path="/subscribe" element={<SubscriptionPage />} />
      <Route path="/payment-success" element={<PaymentErrorBoundary><PaymentSuccess /></PaymentErrorBoundary>} />
      <Route path="/payment-paymongo-result" element={<PaymentErrorBoundary><PayMongoResult /></PaymentErrorBoundary>} />
      <Route path="/admin/fx-rate" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <Layout><AdminFxRate /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin/paymongo-keys" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <Layout><AdminPaymongoKeys /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin/reminders" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <Layout><AdminReminders /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin/schedule-repair" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <Layout><AdminScheduleRepair /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin/data-integrity" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <Layout><AdminDataIntegrity /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin/untagged-alerts" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <Layout><AdminUntaggedAlerts /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/my-salary" element={
        <ProtectedRoute allowedRoles={["tutor"]}>
          <Layout><MySalary /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/trial-bookings" element={
        <ProtectedRoute adminOnly>
          <Layout><TrialBookings /></Layout>
        </ProtectedRoute>
      } />

      {/* Student Portal Route - Student Only */}
      <Route path="/student-dashboard" element={
        <StudentRoute>
          <StudentDashboard />
        </StudentRoute>
      } />
      <Route path="/refer" element={
        <StudentRoute>
          <ReferralPage />
        </StudentRoute>
      } />

      <Route path="/admin-payments" element={
        <ProtectedRoute adminOnly>
          <Layout><AdminPayments /></Layout>
        </ProtectedRoute>
      } />


      {/* Admin-only routes */}
      <Route path="/" element={
        <ProtectedRoute adminOnly>
          <Layout><Dashboard /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/students" element={
        <ProtectedRoute adminOnly>
          <Layout><Students /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/students/:id" element={
        <ProtectedRoute adminOnly>
          <Layout><StudentDetail /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/student-accounts" element={
        <ProtectedRoute adminOnly>
          <Layout><StudentAccounts /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/inactive-students" element={
        <ProtectedRoute adminOnly>
          <Layout><InactiveStudents /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/active-students" element={
        <ProtectedRoute adminOnly>
          <Layout><ActiveStudents /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/deleted-students" element={
        <ProtectedRoute adminOnly>
          <Layout><DeletedStudents /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/teachers" element={
        <ProtectedRoute adminOnly>
          <Layout><Teachers /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/salary-tracker/:teacherId" element={
        <ProtectedRoute adminOnly>
          <Layout><SalaryTracker /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/time-management" element={
        <ProtectedRoute adminOnly>
          <Layout><TimeManagement /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/users" element={
        <ProtectedRoute adminOnly>
          <Layout><UserManagement /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/settings" element={
        <ProtectedRoute adminOnly>
          <Layout><Settings /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/recruitment" element={
        <ProtectedRoute adminOnly>
          <Layout><Recruitment /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/recruitment/:id" element={
        <ProtectedRoute adminOnly>
          <Layout><RecruitmentDetail /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/sales-analytics" element={
        <ProtectedRoute adminOnly>
          <Layout><SalesAnalytics /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin-tutor-profiles" element={
        <ProtectedRoute adminOnly>
          <Layout><AdminTutorProfiles /></Layout>
        </ProtectedRoute>
      } />

      {/* Tutor accessible routes (Admin + Tutor, NOT Student) */}
      <Route path="/time-clock" element={
        <ProtectedRoute allowedRoles={["admin", "tutor"]}>
          <Layout><TimeClock /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/schedule" element={
        <ProtectedRoute allowedRoles={["admin", "tutor"]}>
          <Layout><Schedule /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/my-availability" element={
        <ProtectedRoute allowedRoles={["admin", "tutor"]}>
          <Layout><MyAvailability /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/tutor-messages" element={
        <ProtectedRoute allowedRoles={["admin", "tutor"]}>
          <Layout><MessagesRouter /></Layout>
        </ProtectedRoute>
      } />

      {/* Catch all */}
      <Route path="*" element={<Navigate to={getDefaultRoute()} replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
